import {Box, Grid, Typography} from '@yandex-delivery/blocks'
import {useI18n} from '@yandex-delivery/core/i18n'
import {useMountEffect} from '@yandex-delivery/hooks'
import type {ReactElement} from 'react'
import {useController} from 'react-hook-form'

import {useFetchPickupPointsDesktop, useResolvePickupPoint} from '../../api/address/services/pickupPointService/pickupPoint'
import {Body, PhoneInput} from '../../components/desktop'
import {EventGoal, useFormUserEventHandler} from '../../lib/metrika'
import {toHeaderWidget} from '../../lib/to-header-widget'
import * as classes from '../Address/components/AddressWidget/AddressWidget@desktop.classes'
import type {Props} from '../Address/components/AddressWidget/types'
import * as bodyClasses from '../Address/components/Details/Contact@desktop.classes'
import {renderWidget} from '../index@desktop'
import {CitySuggest} from './CitySuggest/CitySuggest'
import type {ChangeHandler} from './CitySuggest/types'
import {PointSelect} from './PointSelect/PointSelect'

export function PickupWidget({config}: Props): ReactElement {
  const i18n = useI18n()
  const isSource = config.route_point_index === 0
  const name = isSource ? 'ndd.route.source' : 'ndd.route.destination'
  const {mutate, data: options, isPending} = useFetchPickupPointsDesktop(isSource, name)
  const {field} = useController({name: `${name}.city`})

  const changeHandler: ChangeHandler = suggestValue => {
    field.onChange(suggestValue)
    mutate(suggestValue)
  }

  useMountEffect(() => {
    // подгружаю точки для выбранного города, если он есть в черновике или из предзаполнения
    if (field.value) {
      mutate(field.value)
    }
  })

  useResolvePickupPoint(name, isSource)

  const handleReachGoal = useFormUserEventHandler()

  return (
    <>
      <Grid xs={12} item>
        <Box ml="s" mt="s">
          {renderWidget(toHeaderWidget(config))}
        </Box>
      </Grid>
      <Grid container>
        <Grid xs={12} item>
          <div className={classes.root}>
            <Body
              lead={
                <Typography group="tight" mt="m" thickness="medium" variant="body2">
                  {i18n('ndd.point.point-selector.city', 'Город')}
                </Typography>
              }
              tail={
                <CitySuggest
                  dataTestId={isSource ? 'ndd.suggest_city_source' : 'ndd.suggest_city_destination'}
                  name={`${name}.city`}
                  onChange={changeHandler}
                  pointType={isSource ? 'source' : 'destination'}
                />
              }
            />

            <Body
              className={bodyClasses.root}
              lead={
                <Typography group="tight" mt="m" thickness="medium" variant="body2">
                  {i18n('ndd.point.point-selector.dropoff_point', 'Пункт приёма')}
                </Typography>
              }
              tail={
                <PointSelect
                  dataTestId={isSource ? 'ndd.selector_dropoff_point' : 'ndd.selector_pickup_point'}
                  isPending={isPending}
                  isSource={isSource}
                  name={name}
                  options={options}
                  placeholder={i18n('ndd.point.point-selector.select_dropoff_point', 'Выберите пункт приёма')}
                  trailButtonCaption={i18n(
                    'ndd.delivery-method.field.pickup-point.reception-point.button',
                    'Все пункты',
                  )}
                />
              }
            />

            <Body
              className={bodyClasses.root}
              lead={
                <Typography group="tight" mt="m" thickness="medium" variant="body2">
                  {isSource
                    ? i18n('cargo.create.form.source', 'Отправитель')
                    : i18n('cargo.create.destinations.recipient', 'Получатель')}
                </Typography>
              }
              tail={
                <PhoneInput
                  dataTestId={isSource ? 'ndd.contact_phone_source' : 'ndd.contact_phone_destination'}
                  name={isSource ? 'ndd.route.source.phone' : 'ndd.route.destination.phone'}
                  onFocus={() => {
                    handleReachGoal(isSource ? EventGoal.AddressFromPhoneClick : EventGoal.AddressToPhoneClick)
                  }}
                  placeholder={i18n('cargo.create.contact.phone', 'Телефон')}
                />
              }
            />
          </div>
        </Grid>
      </Grid>
    </>
  )
}
